import { createSlice } from "@reduxjs/toolkit";
import { hideLoading, showLoading } from "react-redux-loading-bar";

import * as Api from "./staffAPI";

/**
 * The two states an operator will actually hit get named, because the generic message wastes the
 * time of the person least able to diagnose it. A 404 here is the backend feature flag being off,
 * not a missing account: the controllers are `@ConditionalOnProperty`, so with the flag down the
 * routes are never registered and every path answers 404 with no body to explain itself.
 */
export const staffErrorMessage = (error) => {
  const status = error.response?.status;
  if (status === 404 && !error.response?.data) {
    return "Staff administration is switched off in this environment. It stays unavailable until parkit.rbac.staff-admin.enabled is turned on in parkit-user-store.";
  }
  if (status === 403) {
    return "Your account does not hold Super Admin, so it cannot view or change staff accounts.";
  }
  return (
    error.response?.data?.detail ??
    error.response?.data?.message ??
    error.response?.data?.error ??
    error.message ??
    "Request failed"
  );
};

const initialState = {
  accounts: [],
  total: 0,
  page: 0,
  size: 25,
  search: "",
  status: "idle",
  roles: [],
  rolesStatus: "idle",
  error: "",
  successMessage: "",
  warningMessage: "",
  createModalOpen: false,
  rolesFor: null,
  confirm: null,
  pendingAction: "",
  invitation: null,
};

export const staffSlice = createSlice({
  name: "staff",
  initialState,
  reducers: {
    setStaffPage: (state, action) => {
      state.accounts = action.payload.content;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.size = action.payload.size;
    },
    setStatus: (state, action) => {
      state.status = action.payload;
    },
    setRoles: (state, action) => {
      state.roles = action.payload;
      state.rolesStatus = "succeeded";
    },
    setRolesStatus: (state, action) => {
      state.rolesStatus = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setSize: (state, action) => {
      state.size = action.payload;
      state.page = 0;
    },
    setSearch: (state, action) => {
      state.search = action.payload;
      state.page = 0;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setWarningMessage: (state, action) => {
      state.warningMessage = action.payload;
    },
    setCreateModalOpen: (state, action) => {
      state.createModalOpen = action.payload;
    },
    setRolesFor: (state, action) => {
      state.rolesFor = action.payload;
    },
    setConfirm: (state, action) => {
      state.confirm = action.payload;
    },
    setPendingAction: (state, action) => {
      state.pendingAction = action.payload;
    },
    setInvitation: (state, action) => {
      state.invitation = action.payload;
    },
    replaceAccount: (state, action) => {
      const index = state.accounts.findIndex(({ id }) => id === action.payload.id);
      if (index >= 0) state.accounts[index] = action.payload;
      if (state.rolesFor?.id === action.payload.id) state.rolesFor = action.payload;
    },
  },
});

export const {
  setStaffPage,
  setStatus,
  setRoles,
  setRolesStatus,
  setPage,
  setSize,
  setSearch,
  setError,
  setSuccessMessage,
  setWarningMessage,
  setCreateModalOpen,
  setRolesFor,
  setConfirm,
  setPendingAction,
  setInvitation,
  replaceAccount,
} = staffSlice.actions;

export const selectStaff = (state) => state.staff;

export const handleGetStaff = () => async (dispatch, getState) => {
  const { page, size, search } = getState().staff;
  dispatch(setStatus("loading"));
  dispatch(setError(""));
  try {
    const response = await Api.getStaff({ page, size, search });
    dispatch(setStaffPage(response.data));
    dispatch(setStatus("succeeded"));
  } catch (error) {
    dispatch(setError(staffErrorMessage(error)));
    dispatch(setStatus("failed"));
  }
};

export const handleGetStaffRoles = () => async (dispatch) => {
  dispatch(setRolesStatus("loading"));
  try {
    const response = await Api.getStaffRoles();
    dispatch(setRoles(response.data));
  } catch (error) {
    dispatch(setRolesStatus("failed"));
    dispatch(setError(staffErrorMessage(error)));
  }
};

/**
 * Provisioning can half-succeed, so the outcome is reported in three parts rather than as a
 * boolean: whether the account exists, whether an earlier failed attempt was repaired instead of
 * duplicated, and whether the invitation actually went out. The last one decides whether anybody
 * can use the account, so it is escalated to a warning the operator has to dismiss.
 */
export const handleCreateStaff = (request) => async (dispatch) => {
  dispatch(showLoading());
  dispatch(setPendingAction("create"));
  dispatch(setError(""));
  try {
    const response = await Api.createStaff(request);
    const { account, invitationSent, invitationExpiresInHours, repaired } = response.data;
    dispatch(setCreateModalOpen(false));
    dispatch(setInvitation({
      username: account.username,
      invitationSent,
      invitationExpiresInHours,
      repaired,
    }));
    await dispatch(handleGetStaff());
    return true;
  } catch (error) {
    dispatch(setError(staffErrorMessage(error)));
    return false;
  } finally {
    dispatch(setPendingAction(""));
    dispatch(hideLoading());
  }
};

const mutation = (key, call, describe) => (...args) => async (dispatch) => {
  dispatch(showLoading());
  dispatch(setPendingAction(`${key}:${args[0]}`));
  dispatch(setError(""));
  try {
    const response = await call(...args);
    const { account, effectiveWithinMinutes, sessionsTerminated } = response.data;
    dispatch(replaceAccount(account));
    dispatch(setConfirm(null));
    dispatch(setSuccessMessage(describe(account, effectiveWithinMinutes)));
    // Only `false` is a problem. `null` means the API did not attempt termination because nothing
    // about the account's authority changed, and warning about that would raise an alarm over an
    // operation that worked exactly as intended.
    if (sessionsTerminated === false) {
      dispatch(setWarningMessage(
        `${account.username} could not be signed out, so their existing sessions can keep refreshing. Check Keycloak sessions for this account before relying on this change.`
      ));
    }
    return true;
  } catch (error) {
    dispatch(setError(staffErrorMessage(error)));
    return false;
  } finally {
    dispatch(setPendingAction(""));
    dispatch(hideLoading());
  }
};

export const handleGrantRoles = mutation(
  "grant",
  Api.grantStaffRoles,
  (account, minutes) =>
    `Granted to ${account.username}. Applies within ${minutes} minutes, or immediately if they sign out and back in.`
);

export const handleRevokeRole = mutation(
  "revoke",
  Api.revokeStaffRole,
  (account, minutes) =>
    `Revoked from ${account.username}. They keep the removed access for up to ${minutes} minutes.`
);

export const handleChangePrimaryRole = mutation(
  "primary",
  Api.changeStaffPrimaryRole,
  (account, minutes) =>
    `${account.username} is now ${account.primaryRole}. Applies within ${minutes} minutes.`
);

export const handleEnableStaff = mutation(
  "enable",
  Api.enableStaff,
  (account) => `Enabled ${account.username}. They can sign in now.`
);

export const handleDisableStaff = mutation(
  "disable",
  Api.disableStaff,
  (account, minutes) =>
    `Disabled ${account.username}. Sign-in is blocked now, but an already-issued token stays valid for up to ${minutes} minutes.`
);

const passwordEmail = (key, call, sent, failed) => (id) => async (dispatch) => {
  dispatch(showLoading());
  dispatch(setPendingAction(`${key}:${id}`));
  dispatch(setError(""));
  try {
    const response = await call(id);
    const { account, invitationSent, invitationExpiresInHours } = response.data;
    dispatch(replaceAccount(account));
    dispatch(setConfirm(null));
    if (invitationSent) {
      dispatch(setSuccessMessage(sent(account, invitationExpiresInHours)));
    } else {
      dispatch(setWarningMessage(failed(account)));
    }
    return invitationSent;
  } catch (error) {
    dispatch(setError(staffErrorMessage(error)));
    return false;
  } finally {
    dispatch(setPendingAction(""));
    dispatch(hideLoading());
  }
};

export const handleResetPassword = passwordEmail(
  "reset",
  Api.resetStaffPassword,
  (account, hours) =>
    `Password reset email sent to ${account.email || account.username}. The link expires in ${hours} hours.`,
  (account) =>
    `Keycloak would not send the password reset email to ${account.email || account.username}. Check the account has an email address and that the realm's SMTP settings work, then try again.`
);

export default staffSlice.reducer;
